import { set } from 'lodash';
import { useMemo, useRef } from 'react';
import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form';
import {
  type AnySpec,
  type KindMap,
  type KindOf,
  readField,
  type SpecOf,
  type UnitValues
} from 'v5/core/form-schema/formKinds.types';
import type { FormKit } from './schemaForm.types';

/**
 * One field of a form to check: where the form keeps its value, its spec,
 * and the values of its unit (or list row), keyed by field key.
 */
export type FieldCheck<M extends KindMap<M>> = {
  path: string;
  spec: AnySpec<M>;
  values: UnitValues<M>;
};

type Problem = { at: string | undefined; code: string; message: string };

/** A field's value through its own kind's rules and words. */
function problemOf<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>,
  values: UnitValues<M>
): Problem | null {
  const field = kit[spec.kind];
  const result = field.validate(spec, readField<M, K>(values, spec));
  if (result.type === 'valid') return null;
  return {
    at: result.issue.at,
    code: result.issue.code,
    message: field.message(result.issue, spec)
  };
}

/**
 * A react-hook-form resolver: every field `checks` finds in the form goes
 * through its kind's rules (`kit`); a problem lands at the field's path, or
 * inside it (`issue.at`) where the field has a control for that part.
 */
export function createFieldsResolver<
  M extends KindMap<M>,
  F extends FieldValues
>(checks: (values: F) => FieldCheck<M>[], kit: FormKit<M>): Resolver<F> {
  return async values => {
    const errors: FieldErrors<F> = {};
    let invalid = false;
    for (const check of checks(values)) {
      const problem = problemOf(kit, check.spec, check.values);
      if (problem === null) continue;
      invalid = true;
      set(
        errors,
        problem.at === undefined ? check.path : `${check.path}.${problem.at}`,
        { type: problem.code, message: problem.message }
      );
    }
    return invalid ? { values: {}, errors } : { values, errors: {} };
  };
}

/**
 * A stable resolver over the latest `checks` and `kit`: react-hook-form
 * keeps the resolver it was created with.
 */
export function useFieldsResolver<M extends KindMap<M>, F extends FieldValues>(
  checks: (values: F) => FieldCheck<M>[],
  kit: FormKit<M>
): Resolver<F> {
  const latest = useRef({ checks, kit });
  latest.current = { checks, kit };
  return useMemo<Resolver<F>>(
    () =>
      (...args) =>
        createFieldsResolver<M, F>(
          latest.current.checks,
          latest.current.kit
        )(...args),
    []
  );
}
